import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Car, Loader2 } from 'lucide-react';
import { ACTIVE_SESSION_KEY } from '@/hooks/useActiveSession';
import { claimAutocharge, dismissAutocharge, fetchMyVehicles } from '@/lib/api';
import { toast } from '@/store/toast';
import styles from './AutochargeOfferCard.module.css';

/**
 * The one-time "Link this car for plug & charge?" card.
 *
 * Rendered in flow on the charging screen (never bottom-anchored) when the
 * server says this live session was preceded by the car announcing its
 * own id at the plug. The client never sees that id: the claim sends the
 * session id and the server re-derives the car from the charger log.
 * "Not now" dismisses on the server so the card does not re-pop on
 * another device. `pendingOnLink` (server-judged: this session carries no
 * grant) swaps the promise of plug & charge next time for the pending one.
 */
export function AutochargeOfferCard({ sessionId, pendingOnLink = false }: { sessionId: string; pendingOnLink?: boolean }) {
  const { t } = useTranslation('charging');
  const qc = useQueryClient();
  const vehiclesQ = useQuery({ queryKey: ['my-vehicles'], queryFn: fetchMyVehicles, staleTime: 60_000 });
  const vehicles = vehiclesQ.data ?? [];
  const [vehicleId, setVehicleId] = useState<string>('');
  const [busy, setBusy] = useState<'link' | 'dismiss' | null>(null);

  const refresh = () => qc.invalidateQueries({ queryKey: ACTIVE_SESSION_KEY });

  const link = async (): Promise<void> => {
    setBusy('link');
    try {
      const res = await claimAutocharge({ session_id: sessionId, ...(vehicleId ? { vehicle_id: vehicleId } : {}) });
      toast(res.status === 'pending' ? t('autocharge.linkedPending') : t('autocharge.linked'), { tone: 'accent' });
      await refresh();
    } catch (err) {
      toast(err instanceof Error ? err.message : t('autocharge.linkError'), { tone: 'error' });
      setBusy(null);
    }
  };

  const dismiss = async (): Promise<void> => {
    setBusy('dismiss');
    try {
      await dismissAutocharge(sessionId);
      await refresh();
    } catch {
      setBusy(null);
    }
  };

  return (
    <section className={styles.card} aria-labelledby="autocharge-offer-title">
      <div className={styles.head}>
        <span className={styles.icon} aria-hidden><Car size={18} /></span>
        <div>
          <h2 id="autocharge-offer-title" className={styles.title}>{t('autocharge.title')}</h2>
          <p className={styles.desc}>{t(pendingOnLink ? 'autocharge.descPending' : 'autocharge.desc')}</p>
        </div>
      </div>
      {vehicles.length > 0 && (
        <label className={styles.pick}>
          <span className={styles.pickLabel}>{t('autocharge.whichCar')}</span>
          <select
            className={styles.select}
            value={vehicleId}
            onChange={(e) => setVehicleId(e.target.value)}
            disabled={busy !== null}
          >
            <option value="">{t('autocharge.noVehicle')}</option>
            {vehicles.map((v) => (
              <option key={v._id} value={v._id}>{v.year} {v.make} {v.model}</option>
            ))}
          </select>
        </label>
      )}
      <div className={styles.actions}>
        <button type="button" className={styles.secondary} onClick={() => void dismiss()} disabled={busy !== null}>
          {t('autocharge.notNow')}
        </button>
        <button type="button" className={styles.primary} onClick={() => void link()} disabled={busy !== null}>
          {busy === 'link'
            ? <><Loader2 size={14} className={styles.spin} /> {t('autocharge.linking')}</>
            : t('autocharge.link')}
        </button>
      </div>
    </section>
  );
}
